import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ErrorAlert, ListEmpty, LoadingRows } from '../components/work/page-states';
import { ReadinessBar } from '../components/work/ServiceRequestBadges';
import ServiceRequestShell from '../components/work/ServiceRequestShell';
import { titleLinkClass } from '../components/work/table-cells';
import { useOverlays } from '../components/work/overlay-context';
import { apiErrorMessage } from '../lib/api';
import { formatDateOnly } from '../lib/dates';
import { isOverdue, startOfDay } from '../lib/assigned-queue';
import {
  createImplementation,
  fetchImplementations,
  implementationPath,
  SR_PANEL,
  type ImplementationSlim,
} from '../lib/service-request-views';
import { cn } from '../lib/utils';

function NewImplementationDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (item: ImplementationSlim) => void;
}) {
  const [name, setName] = useState('');
  const [customer, setCustomer] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setCustomer('');
    setTargetDate('');
    setDescription('');
    setError(null);
  }, [open]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (name.trim().length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createImplementation({
        name: name.trim(),
        customer: customer.trim() || null,
        target_date: targetDate || null,
        description: description.trim() || null,
      });
      onCreated(created);
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create the implementation'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={(event) => void onSubmit(event)} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New implementation</DialogTitle>
            <DialogDescription>
              Group related Service Requests under one customer implementation to track overall readiness.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="impl-name">Name</Label>
              <Input
                id="impl-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="UCL Connected Retail Deployment"
                required
                autoFocus
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="impl-customer">Customer</Label>
                <Input id="impl-customer" value={customer} onChange={(event) => setCustomer(event.target.value)} placeholder="UCL" />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="impl-target">Target date</Label>
                <Input id="impl-target" type="date" value={targetDate} onChange={(event) => setTargetDate(event.target.value)} />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="impl-description">Description</Label>
              <Textarea
                id="impl-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={3}
                placeholder="What this implementation delivers for the customer."
              />
            </div>
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || name.trim().length === 0}>
              {saving ? 'Creating…' : 'Create implementation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ServiceRequestImplementations() {
  const navigate = useNavigate();
  const { pushBlock, popBlock } = useOverlays();
  const [items, setItems] = useState<ImplementationSlim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const today = startOfDay(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchImplementations());
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load implementations'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function setDialogOpen(open: boolean) {
    setCreating(open);
    if (open) pushBlock();
    else popBlock();
  }

  return (
    <ServiceRequestShell
      active="implementations"
    >
      <>
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            New implementation
          </Button>
        </div>

        {error ? <ErrorAlert>{error}</ErrorAlert> : null}

        {loading ? (
          <LoadingRows columns={6} />
        ) : error ? null : items.length === 0 ? (
          <ListEmpty
            title="No implementations yet"
            description="Create one, then link Service Requests to it from each request's details."
            action={
              <Button type="button" size="sm" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                New implementation
              </Button>
            }
          />
        ) : (
          <div className={cn(SR_PANEL, 'overflow-hidden')}>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-0 w-[34%]">Name</TableHead>
                <TableHead className="hidden w-40 md:table-cell">Customer</TableHead>
                <TableHead className="w-32">Target date</TableHead>
                <TableHead className="hidden w-24 text-right sm:table-cell">Requests</TableHead>
                <TableHead className="w-44">Readiness</TableHead>
                <TableHead className="w-24 text-right">Blocked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const overdue = isOverdue(item.target_date, today);
                return (
                  <TableRow key={item.id}>
                    <TableCell className="min-w-0">
                      <div className="truncate">
                        <Link to={implementationPath(item.id)} className={titleLinkClass}>
                          {item.name}
                        </Link>
                      </div>
                      {item.customer ? (
                        <span className="mt-0.5 block truncate text-[11px] text-muted-foreground md:hidden">{item.customer}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="hidden truncate text-xs text-muted-foreground md:table-cell">
                      {item.customer || '—'}
                    </TableCell>
                    <TableCell>
                      {item.target_date ? (
                        <span
                          className={cn(
                            'whitespace-nowrap font-mono text-xs tabular-nums',
                            overdue
                              ? 'inline-flex rounded-md bg-red-50 px-1.5 py-0.5 text-red-700 dark:bg-red-950 dark:text-red-300'
                              : 'text-muted-foreground',
                          )}
                        >
                          {formatDateOnly(item.target_date) ?? item.target_date}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden text-right text-xs tabular-nums text-muted-foreground sm:table-cell">
                      {item.request_count}
                    </TableCell>
                    <TableCell>
                      <ReadinessBar percent={item.readiness_percent} />
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={cn(
                          'font-mono text-xs tabular-nums',
                          item.blocked_count > 0 ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground',
                        )}
                      >
                        {item.blocked_count}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}

        <NewImplementationDialog
          open={creating}
          onOpenChange={setDialogOpen}
          onCreated={(created) => {
            setItems((current) => [created, ...current]);
            navigate(implementationPath(created.id));
          }}
        />
      </>
    </ServiceRequestShell>
  );
}
